import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';

const SystemLogsPage = () => {
  const { currentUser } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const refreshInterval = useRef(null);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 100,
    total: 0,
    total_pages: 0
  });

  const [filters, setFilters] = useState({
    level: '',
    source: '',
    search: '',
    start_date: '',
    end_date: ''
  });

  const fetchLogs = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.page_size);
      if (filters.level) params.append('level', filters.level);
      if (filters.source) params.append('source', filters.source);
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`${API}/super-admin/system-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch system logs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.page_size]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/system-logs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch system log stats:', error);
    }
  };

  const fetchSources = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/system-logs/sources`);
      setSources(response.data.sources || []);
    } catch (error) {
      console.error('Failed to fetch log sources:', error);
    }
  };

  useEffect(() => {
    fetchLogs(1);
    fetchStats();
    fetchSources();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => fetchLogs(1), 400);
    return () => clearTimeout(debounce);
  }, [filters]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      refreshInterval.current = setInterval(() => {
        fetchLogs(1);
        fetchStats();
      }, 10000);
    }
    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    };
  }, [autoRefresh, fetchLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ level: '', source: '', search: '', start_date: '', end_date: '' });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const getLevelBadge = (level) => {
    const styles = {
      DEBUG: 'bg-gray-100 text-gray-600',
      INFO: 'bg-blue-50 text-blue-700 border border-blue-200',
      WARNING: 'bg-amber-50 text-amber-700 border border-amber-200',
      ERROR: 'bg-red-50 text-red-700 border border-red-200',
      CRITICAL: 'bg-red-100 text-red-800 border border-red-300 font-bold',
    };
    return (
      <span data-testid={`log-level-badge-${level?.toLowerCase()}`}
        className={`px-2 py-0.5 text-xs font-medium rounded ${styles[level] || 'bg-gray-100 text-gray-600'}`}>
        {level}
      </span>
    );
  };

  if (currentUser?.access_level !== 1 && currentUser?.access_level !== undefined) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900">Access Restricted</h3>
          <p className="text-gray-500 mt-1">Only Level 1 users can view system logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="system-logs-page" className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">System Logs</h1>
              <p className="text-purple-200 text-sm">Runtime application logs — schedulers, services, API calls, errors</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="auto-refresh-toggle"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                autoRefresh
                  ? 'bg-white/20 text-white border border-white/30'
                  : 'bg-white/10 text-purple-200 border border-white/20 hover:bg-white/20'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`} />
              {autoRefresh ? 'Live' : 'Auto-refresh off'}
            </button>
            <button
              data-testid="refresh-logs-btn"
              onClick={() => { fetchLogs(1); fetchStats(); }}
              className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg text-sm font-medium hover:bg-white/20 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Stored</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total_logs?.toLocaleString()}</div>
            <div className="text-xs text-gray-400 mt-1">7-day retention</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last 24h</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.last_24h?.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last Hour</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.last_1h?.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-4 bg-amber-50">
            <div className="text-xs font-medium text-amber-600 uppercase tracking-wider">Warnings (24h)</div>
            <div className="text-2xl font-bold text-amber-700 mt-1">{stats.warnings_24h?.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4 bg-red-50">
            <div className="text-xs font-medium text-red-600 uppercase tracking-wider">Errors (24h)</div>
            <div className="text-2xl font-bold text-red-700 mt-1">{stats.errors_24h?.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Top Sources */}
      {stats?.top_sources?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Sources (24h)</h3>
          <div className="flex flex-wrap gap-2">
            {stats.top_sources.slice(0, 8).map(s => (
              <button
                key={s.source}
                onClick={() => handleFilterChange('source', s.source)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  filters.source === s.source
                    ? 'bg-purple-100 border-purple-300 text-purple-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {s.source} <span className="font-semibold ml-1">{s.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <input
              data-testid="log-search-input"
              type="text"
              placeholder="Search log messages..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              data-testid="log-level-filter"
              value={filters.level}
              onChange={(e) => handleFilterChange('level', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Levels</option>
              <option value="INFO">INFO</option>
              <option value="WARNING">WARNING</option>
              <option value="ERROR">ERROR</option>
              <option value="CRITICAL">CRITICAL</option>
            </select>
          </div>
          <div>
            <select
              data-testid="log-source-filter"
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Sources</option>
              {sources.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <button
              data-testid="clear-filters-btn"
              onClick={clearFilters}
              className="px-3 py-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              title="Clear filters"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Top Pagination */}
        {!isLoading && logs.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{((pagination.page - 1) * pagination.page_size) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(pagination.page * pagination.page_size, pagination.total)}</span> of{' '}
              <span className="font-medium">{pagination.total?.toLocaleString()}</span> logs
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchLogs(pagination.page - 1)}
                disabled={!pagination.has_prev}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  pagination.has_prev
                    ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              <select
                data-testid="page-select-top"
                value={pagination.page}
                onChange={(e) => fetchLogs(Number(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {Array.from({ length: pagination.total_pages || 1 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>Page {i + 1}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500">of {pagination.total_pages}</span>
              <button
                onClick={() => fetchLogs(pagination.page + 1)}
                disabled={!pagination.has_next}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  pagination.has_next
                    ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
              <span className="ml-3 text-gray-600">Loading logs...</span>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-base font-medium">No logs found</p>
              <p className="text-sm mt-1">Logs will appear within 30 seconds of server activity</p>
            </div>
          ) : (
            <table data-testid="system-logs-table" className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-44">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Level</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log, idx) => (
                  <React.Fragment key={idx}>
                    <tr
                      data-testid={`log-row-${idx}`}
                      onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
                      className={`cursor-pointer transition-colors ${
                        log.level === 'ERROR' || log.level === 'CRITICAL'
                          ? 'bg-red-50/40 hover:bg-red-50'
                          : log.level === 'WARNING'
                          ? 'bg-amber-50/30 hover:bg-amber-50/60'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-500 font-mono">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        {getLevelBadge(log.level)}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-600 font-mono truncate max-w-[200px]" title={log.logger}>
                        {log.logger}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-800 truncate max-w-xl" title={log.message}>
                        {log.message}
                      </td>
                    </tr>
                    {expandedRow === idx && (
                      <tr className="bg-gray-50">
                        <td colSpan={4} className="px-4 py-3">
                          <div className="bg-white rounded-lg p-3 border border-gray-200 text-xs font-mono whitespace-pre-wrap text-gray-700 max-h-64 overflow-y-auto">
                            {log.message}
                            {log.source && (
                              <div className="mt-2 text-gray-500">Source: {log.source} (line {log.line_no})</div>
                            )}
                            {log.exception && (
                              <div className="mt-2 text-red-600">
                                Exception: {log.exception.type}: {log.exception.message}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Bottom Pagination */}
        {!isLoading && logs.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{((pagination.page - 1) * pagination.page_size) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(pagination.page * pagination.page_size, pagination.total)}</span> of{' '}
              <span className="font-medium">{pagination.total?.toLocaleString()}</span> logs
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="prev-page-btn"
                onClick={() => fetchLogs(pagination.page - 1)}
                disabled={!pagination.has_prev}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  pagination.has_prev
                    ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              <select
                data-testid="page-select-bottom"
                value={pagination.page}
                onChange={(e) => fetchLogs(Number(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {Array.from({ length: pagination.total_pages || 1 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>Page {i + 1}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500">of {pagination.total_pages}</span>
              <button
                data-testid="next-page-btn"
                onClick={() => fetchLogs(pagination.page + 1)}
                disabled={!pagination.has_next}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  pagination.has_next
                    ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemLogsPage;
